import { jsx as _jsx } from "preact/jsx-runtime";
import { render } from 'preact';
import deepmerge from 'deepmerge';
import { DomTargeter, getContext } from '@searchspring/snap-toolbox';
import { Client } from '@searchspring/snap-client';
import { Logger } from '@searchspring/snap-logger';
import { Tracker } from '@searchspring/snap-tracker';
export class RecommendationInstantiator {
    constructor(config, services, context) {
        this.controller = {};
        this.uses = [];
        this.plugins = [];
        this.middleware = [];
        this.config = config;
        if (!this.config) {
            throw new Error(`Recommendation Instantiator config is required`);
        }
        if (!this.config.config?.branch) {
            throw new Error(`Recommendation Instantiator config must contain 'branch' property`);
        }
        if (!this.config.components || typeof this.config.components != 'object' || !Object.keys(this.config.components).length) {
            throw new Error(`Recommendation Instantiator config must contain 'components' mapping property`);
        }
        if ((!services?.client || !services?.tracker) && !this.config?.client?.globals?.siteId) {
            throw new Error(`Recommendation Instantiator config must contain a valid config.client.globals.siteId value`);
        }
        this.context = deepmerge(context || {}, config.context || {});
        this.client = services?.client || new Client(this.config.client.globals, this.config.client.config);
        this.tracker = services?.tracker || new Tracker(this.config.client.globals);
        this.logger = services?.logger || new Logger('RecommendationInstantiator ');
        const profileCount = {};
        this.targeter = new DomTargeter([
            {
                selector: `script[type="searchspring/recommend"], script[type="searchspring/personalized-recommendations"]${this.config.selector ? ` , ${this.config.selector}` : ''}`,
                inject: {
                    action: 'before',
                    element: (target, origElement) => {
                        const profile = origElement.getAttribute('profile');
                        const recsContainer = document.createElement('div');
                        recsContainer.setAttribute('searchspring-recommend', profile);
                        return recsContainer;
                    },
                },
            },
        ], async (target, injectedElem, elem) => {
            const tag = injectedElem.getAttribute('searchspring-recommend');
            if (!tag) {
                this.logger.warn(`'profile' attribute is missing from <script> tag, skipping this profile`, elem);
                return;
            }
            const contextGlobals = {};
            const elemContext = getContext(['shopperId', 'shopper', 'product', 'seed', 'cart', 'options', 'profile', 'custom'], elem);
            const context = deepmerge(this.context, elemContext);
            const { shopper, shopperId, product, seed, cart, options } = context;
            /*
                type instantiatorContext = {
                    shopper?: {
                        id: string;
                    };
                    shopperId?: string;
                    product?: string;
                    seed?: string;
                    cart?: string[] | () => string[];
                    options?: {
                        siteId?: string;
                        branch?: string;
                        batched?: boolean;
                        realtime?: boolean;
                        categories?: any;
                        limit?: number;
                    }
                }
            */
            if (shopper || shopperId) {
                contextGlobals.shopper = shopper?.id || shopperId;
            }
            if (product || seed) {
                contextGlobals.product = product || seed;
            }
            if (options?.branch) {
                contextGlobals.branch = options.branch;
            }
            if (options?.siteId) {
                contextGlobals.siteId = options.siteId;
            }
            if (options?.categories) {
                contextGlobals.categories = options.categories;
            }
            if (options?.limit && Number.isInteger(Number(options?.limit))) {
                contextGlobals.limits = Number(options?.limit);
            }
            let cartContents;
            if (typeof cart === 'function') {
                try {
                    const cartFuncContents = cart();
                    if (Array.isArray(cartFuncContents)) {
                        cartContents = cartFuncContents;
                    }
                }
                catch (e) {
                    this.logger.warn(`Error getting cart contents from function`, e);
                }
            }
            else if (Array.isArray(cart)) {
                cartContents = cart;
            }
            if (Array.isArray(cartContents)) {
                this.tracker.cookies.cart.set(cartContents);
                contextGlobals.cart = this.tracker.cookies.cart.get();
            }
            profileCount[tag] = profileCount[tag] + 1 || 1;
            const defaultGlobals = {
                limits: 20,
            };
            const globals = deepmerge(deepmerge(defaultGlobals, this.config.client?.globals || {}), contextGlobals);
            const controllerConfig = {
                id: `recommend_${tag}_${profileCount[tag] - 1}`,
                tag,
                batched: options?.batched ?? true,
                realtime: Boolean(options?.realtime),
                ...this.config.config,
                globals,
            };
            const createRecommendationController = (await import('../create/createRecommendationController')).default;
            const controller = createRecommendationController({
                url: this.config.url,
                controller: controllerConfig,
                context,
            }, { client: this.client, tracker: this.tracker });
            this.uses.forEach((attachements) => controller.use(attachements));
            this.plugins.forEach((plugin) => controller.plugin(plugin.func, ...plugin.args));
            this.middleware.forEach((middleware) => controller.on(middleware.event, ...middleware.func));
            await controller.search();
            controller.addTargeter(this.targeter);
            this.controller[controller.config.id] = controller;
            const profileVars = controller.store.profile.display.templateParameters;
            const component = controller.store.profile.display.template?.component;
            if (!profileVars) {
                this.logger.error(`profile '${tag}' found on ${elem} is missing templateParameters!`);
                return;
            }
            if (!component) {
                this.logger.error(`profile '${tag}' found on ${elem} is missing component!`);
                return;
            }
            const RecommendationsComponent = this.config.components[component] && (await this.config.components[component]());
            if (!RecommendationsComponent) {
                this.logger.error(`profile '${tag}' found on ${elem} is expecting component mapping for '${component}' - verify instantiator config.`);
                return;
            }
            setTimeout(() => {
                render(_jsx(RecommendationsComponent, { controller: controller }), injectedElem);
            });
        });
    }
    plugin(func, ...args) {
        this.plugins.push({ func, args });
    }
    on(event, ...func) {
        this.middleware.push({ event, func });
    }
    use(attachments) {
        this.uses.push(attachments);
    }
}
